import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link2, Link2Off } from 'lucide-react';
import { Button, Modal, Select, toast } from '../../components/ui';
import { api, ApiError } from '../../lib/api';
import { useAgreements } from '../agreements/api';
import type { InvoiceLineItem } from './api';

/**
 * WHICH AGREEMENT A LINE BILLED — shown, and changeable, on any invoice.
 *
 * Jesse, on MxD: *"they have an invoice for VTC hours, and there is a VTC
 * agreement. They are not linked."* Relinking moves attribution, never money,
 * so it is offered on sent and paid invoices too; the prepaid hours the invoice
 * bought follow the line (see `InvoicesService.linkLineAgreement`).
 * `Link2` = linked, `Link2Off` = unlinked, the app-wide pair.
 */
export function LineAgreementLink({
  invoiceId,
  clientId,
  line,
  harvestLocked,
}: {
  invoiceId: string;
  clientId: string;
  line: InvoiceLineItem;
  /** Harvest owns this invoice while sync is continuous — show, don't offer. */
  harvestLocked?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [choice, setChoice] = useState(line.agreement?.id ?? '');
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const agreements = useAgreements({ clientId, showArchived: true });

  const save = useMutation({
    mutationFn: (agreementId: string | null) =>
      api.patch(`/invoices/${invoiceId}/lines/${line.id}/agreement`, { agreementId }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['invoices'] });
      void queryClient.invalidateQueries({ queryKey: ['agreements'] });
      void queryClient.invalidateQueries({ queryKey: ['support'] });
      void queryClient.invalidateQueries({ queryKey: ['clients'] });
    },
  });

  const rows = [...(agreements.data?.agreements ?? [])].sort(
    (a, b) => Number(b.isActive) - Number(a.isActive) || a.name.localeCompare(b.name),
  );
  const options = [
    { value: '', label: 'No agreement' },
    ...rows.map((a) => ({ value: a.id, label: a.isActive ? a.name : `${a.name} (archived)` })),
  ];

  const submit = async () => {
    setError(null);
    try {
      await save.mutateAsync(choice || null);
      const name = rows.find((a) => a.id === choice)?.name;
      toast.success(name ? `Line linked to ${name}.` : 'Line unlinked.');
      setOpen(false);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'The link could not be saved.');
    }
  };

  return (
    <>
      <span className="inline-flex items-center gap-1 normal-case">
        {line.agreement ? (
          <>
            <Link2 className="h-3 w-3" aria-hidden />
            <Link to={`/agreements/${line.agreement.id}`} className="hover:underline">
              {line.agreement.name}
            </Link>
          </>
        ) : (
          <>
            <Link2Off className="h-3 w-3" aria-hidden />
            No agreement
          </>
        )}
        {!harvestLocked && (
          <button
            type="button"
            className="ml-1 font-semibold text-primary hover:underline"
            onClick={() => {
              setChoice(line.agreement?.id ?? '');
              setError(null);
              setOpen(true);
            }}
          >
            {line.agreement ? 'Change' : 'Link'}
          </button>
        )}
      </span>
      <Modal
        open={open}
        onOpenChange={(next) => !save.isPending && setOpen(next)}
        title="Link this line to an agreement"
        description="Changes what the line was for — never the amount, the payments or the invoice's status. Any prepaid hours this invoice bought move with it."
        footer={
          <>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={save.isPending}>
              Cancel
            </Button>
            <Button
              onClick={submit}
              loading={save.isPending}
              disabled={choice === (line.agreement?.id ?? '')}
            >
              Save link
            </Button>
          </>
        }
      >
        <p className="mb-3 text-body-sm text-secondary">{line.description}</p>
        <Select
          label="Agreement"
          options={options}
          value={choice}
          onChange={(e) => setChoice(e.target.value)}
          disabled={agreements.isLoading}
          error={error ?? undefined}
        />
      </Modal>
    </>
  );
}
